import { useMutation } from '@tanstack/react-query';
import { Check, Copy } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/toast';
import { useIsPlatformAdmin } from '@/hooks/use-is-platform-admin';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { formatDateTime, formatMoney } from '@/utils/format';

export interface PlatformSmsCheckoutRow {
  id: string;
  churchId: string;
  churchName: string;
  status: string;
  creditAmountMinor: number;
  amountMinor: number;
  currency: string;
  paymentReference: string;
  providerTransactionId: string | null;
  failureCode: string | null;
  paidAt: string | null;
  createdAt: string;
}

type ProviderCheckStatus =
  | 'pending'
  | 'succeeded'
  | 'failed'
  | 'cancelled'
  | 'expired'
  | 'refunded';

interface SmsCheckoutRecheckResult {
  session: PlatformSmsCheckoutRow & {
    ledgerEntryId: string | null;
    testMode: boolean;
    canRetry: boolean;
    checkoutUrl: string | null;
    expiresAt: string;
  };
  providerCheck: {
    status: ProviderCheckStatus;
    confirmedAmountMinor: number | null;
    currency: string | null;
    method: 'mobile_money' | 'card' | null;
    channel: string | null;
    externalReference: string | null;
    feeMinor: number | null;
    providerReference: string | null;
    credentialSource: 'platform' | 'test';
    failureCode: string | null;
    failureReason: string | null;
    providerFailureCode: string | null;
    providerStatusLabel: string | null;
    responseCode: string | null;
    providerMessage: string | null;
    processorResult: 'successful' | 'pending' | 'failed' | 'expired' | 'cancelled' | 'refunded';
  };
  outcome:
    | 'credits_added'
    | 'status_updated'
    | 'unchanged'
    | 'verified'
    | 'provider_pending'
    | 'provider_failed';
}

const statusBadgeVariant = (status: string): BadgeVariant => {
  if (status === 'paid') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'cancelled' || status === 'expired') return 'neutral';
  return 'info';
};

const providerStatusBadgeVariant = (status: ProviderCheckStatus): BadgeVariant => {
  if (status === 'succeeded') return 'success';
  if (status === 'failed' || status === 'cancelled' || status === 'expired' || status === 'refunded') {
    return 'danger';
  }
  return 'info';
};

const DetailRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="grid gap-1 border-b border-border py-3 last:border-b-0 sm:grid-cols-[9rem_1fr] sm:gap-4">
    <dt className="text-body-sm text-fg-secondary">{label}</dt>
    <dd className="text-body text-fg">{children}</dd>
  </div>
);

const CopyableId = ({
  value,
  copyLabel,
  copiedLabel,
  copyFailedLabel,
}: {
  value: string;
  copyLabel: string;
  copiedLabel: string;
  copyFailedLabel: string;
}) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(copiedLabel);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(copyFailedLabel);
    }
  };

  return (
    <div className="flex items-start gap-2">
      <span className="min-w-0 flex-1 break-all font-mono text-body-sm">{value}</span>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="shrink-0"
        aria-label={copyLabel}
        onClick={() => void copy()}
      >
        {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
      </Button>
    </div>
  );
};

const formatMethod = (
  method: SmsCheckoutRecheckResult['providerCheck']['method'],
  channel: string | null | undefined,
  t: (key: string) => string,
): string => {
  if (!method) return '—';
  const label = t(`smsCredits.detail.paymentMethods.${method}`);
  return channel?.trim() ? `${label} · ${channel.trim()}` : label;
};

const resolveFailureMessage = (
  failureCode: string | null,
  failureReason: string | null,
  tErrors: (key: string) => string,
): string | null => {
  if (failureReason) return failureReason;
  if (!failureCode) return null;
  return tErrors(failureCode === 'UNKNOWN' ? 'PAYMENT_FAILED_UNKNOWN' : failureCode);
};

const shouldAutoRecheckOnOpen = (status: string | undefined | null): boolean =>
  status != null && status !== 'paid' && status !== 'cancelled' && status !== 'expired';

export const PlatformSmsCheckoutDetailDialog = ({
  checkout,
  open,
  onOpenChange,
  onRecheckSuccess,
}: {
  checkout: PlatformSmsCheckoutRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRecheckSuccess?: () => void;
}) => {
  const { t } = useTranslation('admin');
  const isAdmin = useIsPlatformAdmin();
  const { t: tCommon } = useTranslation('common');
  const { t: tErrors } = useTranslation('errors');

  const [displayCheckout, setDisplayCheckout] = useState<PlatformSmsCheckoutRow | null>(checkout);
  const [sessionActions, setSessionActions] = useState<{
    canRetry: boolean;
    checkoutUrl: string | null;
  } | null>(null);
  const [providerCheck, setProviderCheck] = useState<SmsCheckoutRecheckResult['providerCheck'] | null>(
    null,
  );
  const [recheckOutcome, setRecheckOutcome] = useState<SmsCheckoutRecheckResult['outcome'] | null>(
    null,
  );
  const autoRecheckStarted = useRef(false);
  const isManualRecheckRef = useRef(false);

  useEffect(() => {
    setDisplayCheckout(checkout);
    setProviderCheck(null);
    setRecheckOutcome(null);
    setSessionActions(null);
    autoRecheckStarted.current = false;
  }, [checkout?.id]);

  const recheck = useMutation({
    mutationFn: (sessionId: string) =>
      api.post<SmsCheckoutRecheckResult>(
        `/platform/sms/credits/checkout-sessions/${sessionId}/recheck`,
        {},
      ),
    onSuccess: (result) => {
      setDisplayCheckout({
        id: result.session.id,
        churchId: result.session.churchId,
        churchName: result.session.churchName,
        status: result.session.status,
        creditAmountMinor: result.session.creditAmountMinor,
        amountMinor: result.session.amountMinor,
        currency: result.session.currency,
        paymentReference: result.session.paymentReference,
        providerTransactionId: result.session.providerTransactionId,
        failureCode: result.session.failureCode,
        paidAt: result.session.paidAt,
        createdAt: result.session.createdAt,
      });
      setSessionActions({
        canRetry: result.session.canRetry,
        checkoutUrl: result.session.checkoutUrl,
      });
      setProviderCheck(result.providerCheck);
      setRecheckOutcome(result.outcome);

      const shouldRefresh =
        result.outcome === 'credits_added' || result.outcome === 'status_updated';
      if (shouldRefresh) {
        onRecheckSuccess?.();
      }

      const shouldToast =
        isManualRecheckRef.current ||
        result.outcome === 'credits_added' ||
        result.outcome === 'status_updated' ||
        result.outcome === 'provider_failed' ||
        result.outcome === 'verified';
      if (shouldToast) {
        toast.success(t(`smsCredits.detail.recheck.outcome.${result.outcome}`));
      }
      isManualRecheckRef.current = false;
    },
    onError: (error) => {
      isManualRecheckRef.current = false;
      const message =
        error instanceof ApiError
          ? tErrors(error.code, { defaultValue: t('smsCredits.detail.recheck.error') })
          : t('smsCredits.detail.recheck.error');
      toast.error(message);
    },
  });

  const activeCheckout = displayCheckout ?? checkout;

  useEffect(() => {
    if (!isAdmin || !open || !checkout || recheck.isPending || autoRecheckStarted.current) return;
    if (!shouldAutoRecheckOnOpen(checkout.status)) return;
    autoRecheckStarted.current = true;
    recheck.mutate(checkout.id);
  }, [open, checkout?.id, checkout?.status, recheck.isPending]);

  const canContinuePayment =
    sessionActions?.canRetry === true && sessionActions.checkoutUrl != null;

  const check = providerCheck;
  const currency = check?.currency ?? activeCheckout?.currency ?? 'GHS';
  const providerTransactionId =
    check?.providerReference ?? activeCheckout?.providerTransactionId ?? null;
  const receiptNumber = check?.externalReference ?? null;
  const confirmedAmountMinor = check?.confirmedAmountMinor ?? null;
  const providerFailureMessage =
    check && check.status !== 'succeeded'
      ? resolveFailureMessage(check.failureCode, check.failureReason, tErrors)
      : null;
  const storedSessionFailure =
    activeCheckout?.failureCode && activeCheckout.status !== 'paid'
      ? activeCheckout.failureCode
      : null;
  const providerPaidButSessionUnresolved =
    check?.status === 'succeeded' && activeCheckout != null && activeCheckout.status !== 'paid';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="md"
        className="overflow-visible sm:max-w-xl"
        closeLabel={tCommon('action.close')}
      >
        <DialogHeader>
          <DialogTitle>
            {activeCheckout
              ? t('smsCredits.detail.dialogTitle', {
                  church: activeCheckout.churchName,
                })
              : t('smsCredits.detail.dialogTitleFallback')}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {activeCheckout ? (
            <dl>
              <DetailRow label={t('smsCredits.columns.church')}>{activeCheckout.churchName}</DetailRow>
              <DetailRow label={t('smsCredits.columns.date')}>
                {formatDateTime(activeCheckout.createdAt)}
              </DetailRow>
              <DetailRow label={t('smsCredits.columns.amount')}>
                <span className="font-medium tabular-nums">
                  {formatMoney(activeCheckout.amountMinor, activeCheckout.currency)}
                </span>
              </DetailRow>
              <DetailRow label={t('smsCredits.columns.status')}>
                <Badge variant={statusBadgeVariant(activeCheckout.status)}>
                  {t(`smsCredits.checkoutStatus.${activeCheckout.status as 'paid'}`)}
                </Badge>
              </DetailRow>
              <DetailRow label={t('smsCredits.columns.paymentReference')}>
                <CopyableId
                  value={activeCheckout.paymentReference}
                  copyLabel={t('smsCredits.detail.copyReference')}
                  copiedLabel={t('smsCredits.detail.copied')}
                  copyFailedLabel={t('smsCredits.detail.copyFailed')}
                />
              </DetailRow>
              {activeCheckout.providerTransactionId && !check ? (
                <DetailRow label={t('smsCredits.detail.providerTransactionId')}>
                  <CopyableId
                    value={activeCheckout.providerTransactionId}
                    copyLabel={t('smsCredits.detail.copyTransactionId')}
                    copiedLabel={t('smsCredits.detail.copied')}
                    copyFailedLabel={t('smsCredits.detail.copyFailed')}
                  />
                </DetailRow>
              ) : null}

              <div className="pt-4">
                <p className="text-body-sm font-medium text-fg">
                  {t('smsCredits.detail.recheck.confirmationSection')}
                </p>
              </div>

              {recheck.isPending && !check ? (
                <p className="py-3 text-body-sm text-fg-secondary">
                  {t('smsCredits.detail.recheck.checking')}
                </p>
              ) : null}

              {check || providerTransactionId || receiptNumber ? (
                <>
                  {check?.providerStatusLabel ? (
                    <DetailRow label={t('smsCredits.detail.recheck.providerStatus')}>
                      <Badge variant={providerStatusBadgeVariant(check.status)}>
                        {check.providerStatusLabel}
                      </Badge>
                    </DetailRow>
                  ) : check ? (
                    <DetailRow label={t('smsCredits.detail.recheck.providerStatus')}>
                      <Badge variant={providerStatusBadgeVariant(check.status)}>
                        {t(`smsCredits.detail.recheck.providerStatuses.${check.status}`)}
                      </Badge>
                    </DetailRow>
                  ) : null}
                  <DetailRow label={t('smsCredits.detail.recheck.processorResult')}>
                    {check ? (
                      <span
                        className={
                          check.processorResult === 'successful'
                            ? 'font-medium text-success-fg'
                            : check.processorResult === 'pending'
                              ? 'text-fg-secondary'
                              : 'text-danger'
                        }
                      >
                        {t(`smsCredits.detail.recheck.processorResults.${check.processorResult}`)}
                      </span>
                    ) : providerFailureMessage ? (
                      <span className="text-danger">{providerFailureMessage}</span>
                    ) : activeCheckout.status === 'paid' ? (
                      <span className="font-medium text-success-fg">
                        {t('smsCredits.detail.recheck.processorResults.successful')}
                      </span>
                    ) : (
                      '—'
                    )}
                  </DetailRow>
                  {check ? (
                    <DetailRow label={t('smsCredits.detail.paymentMethod')}>
                      {formatMethod(check.method, check.channel, t)}
                    </DetailRow>
                  ) : null}
                  {receiptNumber ? (
                    <DetailRow label={t('smsCredits.detail.recheck.receiptNumber')}>
                      <span className="break-all font-mono text-body-sm">{receiptNumber}</span>
                    </DetailRow>
                  ) : null}
                  {providerTransactionId ? (
                    <DetailRow label={t('smsCredits.detail.providerTransactionId')}>
                      <CopyableId
                        value={providerTransactionId}
                        copyLabel={t('smsCredits.detail.copyTransactionId')}
                        copiedLabel={t('smsCredits.detail.copied')}
                        copyFailedLabel={t('smsCredits.detail.copyFailed')}
                      />
                    </DetailRow>
                  ) : null}
                  {check?.feeMinor != null ? (
                    <DetailRow label={t('smsCredits.detail.recheck.providerFee')}>
                      {formatMoney(check.feeMinor, currency)}
                    </DetailRow>
                  ) : null}
                  {confirmedAmountMinor != null ? (
                    <DetailRow label={t('smsCredits.detail.recheck.confirmedAmount')}>
                      {formatMoney(confirmedAmountMinor, currency)}
                    </DetailRow>
                  ) : null}
                  {check?.responseCode ? (
                    <DetailRow label={t('smsCredits.detail.recheck.responseCode')}>
                      <span className="font-mono text-body-sm">{check.responseCode}</span>
                    </DetailRow>
                  ) : null}
                  {check?.providerMessage ? (
                    <DetailRow label={t('smsCredits.detail.recheck.providerMessage')}>
                      <span className="text-body-sm">{check.providerMessage}</span>
                    </DetailRow>
                  ) : null}
                  {check?.providerFailureCode ? (
                    <DetailRow label={t('smsCredits.detail.recheck.providerFailureCode')}>
                      <span className="font-mono text-body-sm">{check.providerFailureCode}</span>
                    </DetailRow>
                  ) : null}
                  {providerFailureMessage ? (
                    <DetailRow label={t('smsCredits.detail.recheck.providerResult')}>
                      <span className="text-danger">{providerFailureMessage}</span>
                    </DetailRow>
                  ) : null}
                  {storedSessionFailure ? (
                    <DetailRow label={t('smsCredits.detail.recheck.storedFailure')}>
                      <span className="font-mono text-body-sm text-danger">{storedSessionFailure}</span>
                    </DetailRow>
                  ) : null}
                  {check ? (
                    <DetailRow label={t('smsCredits.detail.recheck.keysUsed')}>
                      {check.credentialSource === 'test'
                        ? t('smsCredits.detail.recheck.credentialTest')
                        : t('smsCredits.detail.recheck.credentialPlatform')}
                    </DetailRow>
                  ) : null}
                  {providerPaidButSessionUnresolved ? (
                    <DetailRow label={t('smsCredits.detail.recheck.trinityOutcome')}>
                      <span className="text-body-sm text-fg-secondary">
                        {t('smsCredits.detail.recheck.providerPaidSessionPending')}
                      </span>
                    </DetailRow>
                  ) : recheckOutcome ? (
                    <DetailRow label={t('smsCredits.detail.recheck.trinityOutcome')}>
                      {t(`smsCredits.detail.recheck.outcome.${recheckOutcome}`)}
                    </DetailRow>
                  ) : null}
                </>
              ) : (
                <p className="py-3 text-body-sm text-fg-secondary">
                  {t('smsCredits.detail.recheck.confirmationEmpty')}
                </p>
              )}
            </dl>
          ) : null}
        </DialogBody>
        <DialogFooter className="w-full min-w-0 !flex-col gap-2 sm:!flex-row sm:flex-wrap sm:justify-end">
          {canContinuePayment ? (
            <Button
              className="w-full sm:w-auto"
              onClick={() => {
                if (sessionActions?.checkoutUrl) {
                  window.location.assign(sessionActions.checkoutUrl);
                }
              }}
            >
              {t('smsCredits.detail.continuePayment')}
            </Button>
          ) : null}
          {isAdmin && activeCheckout ? (
            <Button
              variant="secondary"
              className="w-full sm:w-auto"
              disabled={recheck.isPending}
              onClick={() => {
                isManualRecheckRef.current = true;
                recheck.mutate(activeCheckout.id);
              }}
            >
              {recheck.isPending
                ? t('smsCredits.detail.recheck.checking')
                : t('smsCredits.detail.recheck.action')}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
